import {
  Box,
  Typography,
  Grid,
  Divider,
  Chip,
  Alert,
  Avatar,
} from "@material-ui/core";
import { LoadingButton } from "@material-ui/lab";
import PropTypes from "prop-types";
import flashFill from "@iconify/icons-eva/flash-fill";
import Iconify from "src/components/iconify";
import { formatearMonedaCliente } from "src/utils/formatearMonedaCliente";
import { BRAND } from "src/theme/brandTokens";

const SeccionRevision = ({ titulo, children }) => (
  <Box sx={{ mb: 3 }}>
    <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1 }}>
      {titulo}
    </Typography>
    {children}
    <Divider sx={{ mt: 2 }} />
  </Box>
);

SeccionRevision.propTypes = {
  titulo: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
};

const CampoRevision = ({ label, value }) => {
  if (!value) return null;
  return (
    <Box sx={{ mb: 1 }}>
      <Typography variant="caption" color="textSecondary">
        {label}
      </Typography>
      <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
        {value}
      </Typography>
    </Box>
  );
};

CampoRevision.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
};

const StepRevision = ({ formik, isGuardando }) => {
  const v = formik.values;

  const errores = [];
  if (!v.vendedor) errores.push("Vendedor no seleccionado");
  if (!v.sucursal) errores.push("Sucursal no seleccionada");
  if (!v.solicitanteId && !v.solicitanteNuevo)
    errores.push("Solicitante no definido");
  if (v.items.length === 0)
    errores.push("Sin fases / servicios en la propuesta");
  const itemsSinCliente = v.items.filter((it) => !it.refCliente);
  if (itemsSinCliente.length > 0)
    errores.push(`${itemsSinCliente.length} ítem(s) sin cliente asignado`);
  const itemsSinResponsable = v.items.filter((it) => !it.refEmpleado);
  if (itemsSinResponsable.length > 0)
    errores.push(
      `${itemsSinResponsable.length} ítem(s) sin responsable asignado`,
    );
  const itemsSinPlan = v.items.filter(
    (it) => it.tipoItem === "PLAN" && !it.refTipoPlan,
  );
  if (itemsSinPlan.length > 0)
    errores.push(
      `${itemsSinPlan.length} ítem(s) PLAN sin servicio del catálogo`,
    );

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Revisión final
      </Typography>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
        Revisa todos los datos antes de guardar la propuesta.
      </Typography>

      {errores.length > 0 && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            Completa los siguientes campos antes de guardar:
          </Typography>
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            {errores.map((e) => (
              <li key={e}>
                <Typography variant="body2">{e}</Typography>
              </li>
            ))}
          </ul>
        </Alert>
      )}

      {/* Portada */}
      <SeccionRevision titulo="Portada">
        <Grid container spacing={1}>
          <Grid item xs={12} sm={6}>
            <CampoRevision label="Título" value={v.titulo} />
            <CampoRevision label="Descripción" value={v.descripcion} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <CampoRevision label="Sucursal" value={v.sucursal} />
            <CampoRevision label="Fecha" value={v.fecha} />
          </Grid>
        </Grid>
        {v.generadoConIA && (
          <Chip
            size="small"
            icon={<Iconify icon={flashFill} width={14} height={14} />}
            label="Generado con IA"
            sx={{
              mt: 1,
              bgcolor: BRAND.color.primaryBg,
              color: BRAND.color.primary,
              border: `1px solid ${BRAND.color.primary}`,
            }}
          />
        )}
      </SeccionRevision>

      {/* Solicitante */}
      <SeccionRevision titulo="Solicitante">
        {v.solicitanteId ? (
          <Typography variant="body2">
            ID existente: {v.solicitanteId}
          </Typography>
        ) : v.solicitanteNuevo ? (
          <Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                flexWrap: "wrap",
              }}
            >
              <Typography variant="body2" component="span">
                {v.solicitanteNuevo.nombre}
              </Typography>
              <Chip label="Nuevo" size="small" />
            </Box>
            {v.solicitanteNuevo.correo && (
              <Typography variant="caption" color="textSecondary">
                {v.solicitanteNuevo.correo}
              </Typography>
            )}
          </Box>
        ) : (
          <Typography variant="body2" color="error">
            No definido
          </Typography>
        )}
      </SeccionRevision>

      {/* Fases */}
      <SeccionRevision titulo={`Fases / Servicios (${v.items.length})`}>
        {v.items.length === 0 ? (
          <Typography variant="body2" color="error">
            Sin fases
          </Typography>
        ) : (
          v.items.map((item, i) => (
            <Box
              key={i}
              sx={{
                mb: 1.5,
                p: 1.5,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  mb: 0.5,
                  flexWrap: "wrap",
                  alignItems: "center",
                }}
              >
                <Typography variant="caption" color="textSecondary">
                  {i + 1}.
                </Typography>
                <Chip
                  label={item.tipoItem}
                  size="small"
                  sx={{
                    bgcolor:
                      item.tipoItem === "PLAN"
                        ? "#E3F2FD"
                        : BRAND.color.primaryBg,
                    color:
                      item.tipoItem === "PLAN"
                        ? "#1565C0"
                        : BRAND.color.primary,
                  }}
                />
                {item.refTipoPlan && (
                  <Chip
                    label={item.refTipoPlan}
                    size="small"
                    variant="outlined"
                    sx={{ fontSize: 10 }}
                  />
                )}
              </Box>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {item.faseTitulo || item.nombreServicio || "Sin nombre"}
              </Typography>
              {item.faseObjetivo && (
                <Typography
                  variant="caption"
                  color="textSecondary"
                  sx={{ display: "block" }}
                >
                  {item.faseObjetivo.slice(0, 80)}
                  {item.faseObjetivo.length > 80 ? "…" : ""}
                </Typography>
              )}
              <Typography variant="caption" color="textSecondary">
                {formatearMonedaCliente(item.tipoMoneda)}{" "}
                {Number(
                  item.valorConDescuento || item.valorOriginal,
                ).toLocaleString("es-CL")}
                {item.descuento > 0 && ` (descuento: ${item.descuento}%)`}
                {item.refCliente && ` · Cliente: ${item.refCliente}`}
              </Typography>
            </Box>
          ))
        )}
      </SeccionRevision>

      {/* Equipo */}
      {v.equipo.length > 0 && (
        <SeccionRevision
          titulo={`Equipo de trabajo (${v.equipo.length} miembros)`}
        >
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
            {v.equipo.map((m, i) => (
              <Box
                key={i}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  p: 1,
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1,
                  minWidth: 160,
                }}
              >
                {m.fotoUrl ? (
                  <Avatar src={m.fotoUrl} sx={{ width: 32, height: 32 }} />
                ) : (
                  <Avatar
                    sx={{
                      width: 32,
                      height: 32,
                      bgcolor: BRAND.color.primary,
                      fontSize: 14,
                    }}
                  >
                    {m.nombre?.[0] || "?"}
                  </Avatar>
                )}
                <Box>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 500, lineHeight: 1.2 }}
                  >
                    {m.nombre || "Sin nombre"}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {m.rolDisplay || "Sin rol"}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </SeccionRevision>
      )}

      {/* Vendedor y sucursal */}
      <SeccionRevision titulo="Vendedor y sucursal">
        <Grid container spacing={1}>
          <Grid item xs={12} sm={4}>
            <CampoRevision
              label="Vendedor"
              value={v.vendedor || "No seleccionado"}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <CampoRevision label="Supervisor" value={v.supervisor || "—"} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <CampoRevision label="Sucursal" value={v.sucursal} />
          </Grid>
        </Grid>
      </SeccionRevision>

      {/* Textos */}
      {(v.antecedentes || v.alcance || v.resumenEjecutivo) && (
        <SeccionRevision titulo="Contenido de la propuesta">
          <CampoRevision label="Antecedentes" value={v.antecedentes} />
          <CampoRevision label="Alcance / Estructura" value={v.alcance} />
          <CampoRevision label="Resumen ejecutivo" value={v.resumenEjecutivo} />
        </SeccionRevision>
      )}

      {/* Info adicional */}
      {(v.condicionesPago || v.duracionEstimada || v.proximosPasos) && (
        <SeccionRevision titulo="Información adicional">
          <CampoRevision
            label="Condiciones de pago"
            value={v.condicionesPago}
          />
          <CampoRevision label="Duración estimada" value={v.duracionEstimada} />
          <CampoRevision label="Próximos pasos" value={v.proximosPasos} />
        </SeccionRevision>
      )}

      {/* Submit */}
      <Box sx={{ mt: 4 }}>
        <LoadingButton
          variant="contained"
          size="large"
          loading={isGuardando}
          disabled={errores.length > 0}
          onClick={formik.submitForm}
          sx={{
            bgcolor: BRAND.color.primary,
            "&:hover": { bgcolor: BRAND.color.primaryDark },
            "&.Mui-disabled": { bgcolor: "action.disabledBackground" },
          }}
        >
          Guardar y finalizar borrador
        </LoadingButton>
      </Box>
    </Box>
  );
};

StepRevision.propTypes = {
  formik: PropTypes.object.isRequired,
  isGuardando: PropTypes.bool.isRequired,
};

export default StepRevision;
